// City panel "Summon Champion" section (D4/D11). Driven by the player's
// `legend` global: a requirements checklist + cost while dormant, a progress
// bar while summoning, a respawn timer while fallen. The button is disabled
// with a reason when a gate fails (same idea as the setup screen's options).

import type { LegendKindRow, SceneNode } from '@aod/protocol';
import { Button } from '@aod/ui/components/ui/button';
import { Progress } from '@aod/ui/components/ui/progress';
import { Check, Crown, X } from 'lucide-react';
import { useState } from 'react';
import { type MessageKey, type Translate, useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { useChampion } from './ChampionContext.js';
import {
  type GateItem,
  cityTierApplies,
  formatTicksClock,
  phylacteryCityOf,
  resummonCostPct,
  summonGate,
  summonProgressPct,
  ticksLeft,
} from './championRules.js';

const REJECTIONS = new Set([
  'cannot_afford',
  'no_summoning_site',
  'city_too_small',
  'doctrine_locked',
  'not_available',
  'not_owner',
]);

export function CitySummonSection({ city }: { city: SceneNode }) {
  const t = useT();
  const api = useChampion();
  const [error, setError] = useState<string | null>(null);
  if (!api) return null;
  const ownerId = city.props?.ownerId as number | undefined;
  // Neutral cities read as ownerId 0 (the human's index); the snapshot flags them.
  if (ownerId !== api.localPlayerIndex || city.props?.neutral === true) return null;

  const { legend, tick, rows } = api;
  const row = api.offered.row;
  const buildings = (city.props?.completedBuildings as string[] | undefined) ?? [];
  const tier = (city.props?.tier as number | undefined) ?? 1;
  const hasSite = buildings.includes(row.siteBuilding);
  const runningHere = legend.state === 'summoning' && legend.cityId === city.id;
  // Discoverable from the kind's min city tier on; once a site exists it always shows.
  // The Lich's phylactery city shows its warning while the Lich lives.
  const phylacheryHere = legend.state === 'alive' && phylacteryCityOf(legend) === city.id;
  const visible =
    hasSite ||
    runningHere ||
    phylacheryHere ||
    (legend.state === 'dormant' && tier >= row.minCityTier);
  if (!visible) return null;

  const kindName = t(`champion.kind.${api.offered.kind}` as MessageKey);
  const gate = summonGate({
    rows,
    row,
    legend,
    doctrineTier: api.doctrineTier,
    gold: api.gold,
    faith: api.faith,
    cityPop: (city.props?.population as number | undefined) ?? 0,
    cityTier: tier,
    completedBuildings: buildings,
  });

  const rareCurrency = gate.items.find((i) => i.key === 'rare')?.currency;

  return (
    <section
      data-testid="champion-summon-section"
      {...appDom({
        id: 'champion-summon-section',
        type: 'Section',
        scope: 'aside',
        label: t('champion.summon.title'),
      })}
      className="flex flex-col gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2"
    >
      <h4 className="flex items-center gap-1.5 text-xs font-semibold">
        <Crown className="size-3.5 text-amber-500" />
        {t('champion.summon.title')}
      </h4>

      {runningHere && (
        <Progress
          value={summonProgressPct(legend.doneTick, tick, row.summonTicks)}
          aria-label={t('champion.summon.title')}
          data-testid="champion-summon-progress"
        />
      )}
      {runningHere && (
        <p className="text-[11px]" data-testid="champion-summon-status">
          {t('champion.summon.progress', {
            pct: summonProgressPct(legend.doneTick, tick, row.summonTicks),
          })}{' '}
          <span className="text-muted-foreground">
            {t('champion.summon.eta', { time: formatTicksClock(ticksLeft(legend.doneTick, tick)) })}
          </span>
        </p>
      )}
      {legend.state === 'summoning' && !runningHere && (
        <p className="text-[11px] text-muted-foreground" data-testid="champion-summon-status">
          {t('champion.summon.elsewhere')}
        </p>
      )}
      {legend.state === 'alive' && (
        <p className="text-[11px] text-muted-foreground" data-testid="champion-summon-status">
          {t('champion.summon.alive')}
        </p>
      )}
      {phylacheryHere && (
        <p
          className="text-[10px] text-red-700 dark:text-red-400"
          data-testid="champion-phylactery-warning"
        >
          {t('champion.summon.phylacheryHere')}
        </p>
      )}
      {legend.state === 'fallen' && (
        <p className="text-[11px]" data-testid="champion-respawn-timer">
          {t('champion.summon.fallen', {
            time: formatTicksClock(ticksLeft(legend.respawnTick, tick)),
          })}
        </p>
      )}

      {legend.state === 'dormant' && (
        <>
          <p className="text-[10px] text-muted-foreground" data-testid="champion-summon-blurb">
            {blurb(t, api.offered.kind)}
          </p>
          {legend.offered && !legend.kind && (
            <p className="text-[10px]" data-testid="champion-summon-offered">
              {t('champion.summon.offeredBy', { kind: kindName })}
            </p>
          )}
          {api.offered.kind === 'lich' && (
            <p
              className="text-[10px] text-red-700 dark:text-red-400"
              data-testid="champion-phylactery-warning"
            >
              {t('champion.summon.phylactery')}
            </p>
          )}
          <ul
            data-testid="champion-summon-checklist"
            aria-label={t('champion.summon.requirements')}
            className="flex flex-col gap-0.5"
          >
            {gate.items.map((item) => (
              <Requirement
                key={item.key}
                item={item}
                row={row}
                firstSummonOnly={!cityTierApplies(rows, legend.deaths)}
              />
            ))}
          </ul>
          <p className="text-[10px]" data-testid="champion-summon-cost">
            {t(rareCurrency === 'faith' ? 'champion.summon.costFaith' : 'champion.summon.cost', {
              gold: gate.cost.gold,
              rare: gate.cost.rare,
              pop: gate.cost.pop,
            })}
            {legend.deaths > 0 &&
              ` ${t('champion.summon.recost', { pct: resummonCostPct(rows, legend.deaths) })}`}
          </p>
          <Button
            type="button"
            size="sm"
            disabled={gate.blocker !== null}
            data-testid="champion-summon-button"
            {...appDom({
              id: 'champion-summon-button',
              type: 'Button',
              role: 'cta',
              scope: 'aside',
              label: t('champion.summon.button', { kind: kindName }),
            })}
            title={
              gate.blocker
                ? t(`champion.block.${gate.blocker}` as MessageKey, gateVars(t, row))
                : undefined
            }
            className="cursor-pointer"
            onClick={() => setError(api.summon(city.id))}
          >
            {t('champion.summon.button', { kind: kindName })}
          </Button>
          {gate.blocker && (
            <p className="text-[10px] text-muted-foreground" data-testid="champion-summon-reason">
              {t(
                `champion.block.${gate.blocker === 'rare' && rareCurrency === 'faith' ? 'rareFaith' : gate.blocker}` as MessageKey,
                gateVars(t, row),
              )}
            </p>
          )}
          {error && (
            <p
              className="text-[10px] text-red-700 dark:text-red-400"
              data-testid="champion-summon-error"
            >
              {t(
                (REJECTIONS.has(error)
                  ? `champion.summon.err.${error}`
                  : 'champion.summon.err.rejected') as MessageKey,
              )}
            </p>
          )}
        </>
      )}
    </section>
  );
}

/** Per-kind flavour line; a kind the UI has no copy for uses the generic one. */
function blurb(t: Translate, kind: string): string {
  const key = `champion.summon.blurb.${kind}` as MessageKey;
  const text = t(key);
  return text === key ? t('champion.summon.blurb') : text;
}

/** Template variables for the requirement / blocker strings, all from the offered row. */
function gateVars(t: Translate, row: LegendKindRow, item?: GateItem) {
  return {
    have: item?.have ?? 0,
    need: item?.need ?? 0,
    doctrines: row.doctrines.map((d) => t(`archetype.${d}` as MessageKey)).join(', '),
    site: siteName(t, row),
  };
}

function siteName(t: Translate, row: LegendKindRow): string {
  const key = `champion.site.${row.siteBuilding}` as MessageKey;
  const name = t(key);
  return name === key ? row.siteBuilding : name;
}

function Requirement({
  item,
  row,
  firstSummonOnly,
}: { item: GateItem; row: LegendKindRow; firstSummonOnly: boolean }) {
  const t = useT();
  // After a death the city-tier minimum no longer gates (D4 "as built"): say so
  // instead of showing "City tier 3 or higher (now 1)" with a tick.
  const key =
    item.key === 'rare' && item.currency === 'faith'
      ? 'champion.req.rareFaith'
      : item.key === 'cityTier' && firstSummonOnly
        ? 'champion.req.cityTierFirst'
        : `champion.req.${item.key}`;
  const label = t(key as MessageKey, gateVars(t, row, item));
  return (
    <li
      data-testid={`champion-req-${item.key}`}
      data-met={item.met ? 'true' : 'false'}
      className={`flex items-center gap-1.5 text-[10px] ${
        item.met ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'
      }`}
    >
      {item.met ? <Check className="size-3" /> : <X className="size-3" />}
      {label}
    </li>
  );
}
